import { useEffect, useState } from "react";

import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons";
import {
  addMinutes,
  endOfDay,
  formatISO,
  parseISO,
  startOfDay,
  subMinutes,
} from "date-fns";
import { v4 as uuidV4 } from "uuid";

import {
  informPreparingDownload,
  pollAndNotifyDownloadSuccess,
} from "src/api/downloadUtils";
import { ExportDecisionsEndpoint } from "src/api/endpoints";
import { DecisionEnvironment } from "src/clients/history_v3";
import { HistoryExportInputFiltersInputEnvironmentEnum } from "src/clients/jobs/api";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { getJobRunFlags, JobRun } from "src/jobs/types";
import { computeRunningTotal } from "src/performance/nodeStatusUtils";
import {
  MAX_HISTORY_WAIT_MS,
  useNodeStatusCounts,
} from "src/performance/queries";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type Props = {
  jobRun: JobRun;
  outputNodeId: string;
};

const useHistoryReady = (jobRun: JobRun, outputNodeId: string): boolean => {
  const { workspace, flow } = useAuthoringContext();
  const { isJobRunning, isJobTerminated } = getJobRunFlags(jobRun.status.type);

  const expectedTotal = isJobTerminated
    ? jobRun.success_rows_count +
      jobRun.failed_rows_count +
      jobRun.ignored_rows_count +
      jobRun.pending_rows_count
    : jobRun.total_rows_count;

  const { data } = useNodeStatusCounts({
    baseUrl: workspace.base_url ?? "",
    jobRunId: jobRun.id,
    flowId: flow.id,
    environment: DecisionEnvironment.TEST,
    fromStartTime: formatISO(subMinutes(parseISO(jobRun.created_at), 60)),
    untilStartTime: formatISO(addMinutes(parseISO(jobRun.updated_at), 60)),
    isRunning: isJobRunning,
    expectedTotal,
    terminatedAt: isJobTerminated ? jobRun.updated_at : undefined,
    outputNodeId,
  });

  const runningTotal = data ? computeRunningTotal(data, outputNodeId) : 0;
  const countsMet = expectedTotal > 0 && runningTotal >= expectedTotal;

  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const elapsed = Date.now() - new Date(jobRun.updated_at).getTime();
    if (elapsed >= MAX_HISTORY_WAIT_MS) {
      setTimedOut(true);
      return;
    }
    const timer = setTimeout(
      () => setTimedOut(true),
      MAX_HISTORY_WAIT_MS - elapsed,
    );
    return () => clearTimeout(timer);
  }, [jobRun.updated_at]);

  return countsMet || timedOut;
};

export const DownloadJobRunOutputButton: React.FC<Props> = ({
  jobRun,
  outputNodeId,
}) => {
  const { flow, workspace } = useAuthoringContext();
  const historyReady = useHistoryReady(jobRun, outputNodeId);

  const handleClick = async () => {
    const toastId = uuidV4();
    try {
      informPreparingDownload(toastId);
      const startDate = formatISO(startOfDay(parseISO(jobRun.created_at)));
      const endDate = formatISO(endOfDay(parseISO(jobRun.updated_at)));
      const downloadJob = await ExportDecisionsEndpoint.exportDecisionsAsync(
        workspace.base_url!,
        {
          flow_id: flow.id,
          request: {
            flow_slug: flow.slug,
            input_filters: {
              job_run_id: jobRun.id,
              start_date: startDate,
              end_date: endDate,
              environment: HistoryExportInputFiltersInputEnvironmentEnum.TEST,
            },
            output_filters: {
              include_extra_fields: false,
              include_response_data: true,
              include_traffic_policy: false,
              include_request_data: true,
            },
            format: "json",
          },
        },
        true,
      );
      await pollAndNotifyDownloadSuccess(
        downloadJob,
        workspace.base_url!,
        toastId,
        ExportDecisionsEndpoint.pollDecisionExport,
      );
    } catch (err) {
      logger.error("Failed to create download dataset job", err);
      toastActions.failure({
        id: toastId,
        title: "Failed to download the run results",
        description: errorMessage(err),
      });
    }
  };

  return (
    <Tooltip
      title="Results are not ready yet"
      disabled={historyReady}
      dataLoc="download-job-run-output-tooltip"
      asChild
    >
      <IconButton
        dataLoc="download-job-run-output-icon"
        disabled={!historyReady}
        icon={faArrowDownToLine}
        size="xs"
        onClick={handleClick}
      />
    </Tooltip>
  );
};
